/**
 * Regeln des Kontaktmoduls (Plan 02 Abschnitt 6) als reine Funktionen, damit sie in Vitest prüfbar sind.
 */

export interface PriceLike {
  year: number;
  season_code?: string | null;
  created_at?: string | Date | null;
  price_per_tree: number;
}

const seasonRank: Record<string, number> = { F: 1, S: 2, H: 3 };

/** R2: Letzter Baumpreis = neuester Eintrag nach (Jahr, Saison, Anlagezeit). */
export function latestPrice<T extends PriceLike>(prices: T[]): T | null {
  if (!prices.length) return null;
  return [...prices].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    const sa = seasonRank[a.season_code ?? ''] ?? 0;
    const sb = seasonRank[b.season_code ?? ''] ?? 0;
    if (sa !== sb) return sb - sa;
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  })[0]!;
}

/** R3: Preis aus dem Protokoll übernehmen, wenn er vom letzten abweicht. */
export function shouldRecordPrice(prices: PriceLike[], newPrice: number): boolean {
  const last = latestPrice(prices);
  if (!last) return true;
  return Math.abs(Number(last.price_per_tree) - Number(newPrice)) >= 0.005;
}

/** R1: Anzeigename */
export function displayName(c: {
  kind: 'person' | 'organisation';
  first_name?: string | null;
  last_name?: string | null;
  org_name?: string | null;
  title?: string | null;
}): string {
  if (c.kind === 'organisation') return (c.org_name ?? '').trim();
  return [c.title, c.first_name, c.last_name]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** R12: Normalisierung für die Duplikaterkennung */
export function normalizeName(s: string | null | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/\b(dr|prof|dipl|ing|frau|herr|forstamt|gmbh|ag|e\.?\s?v)\b\.?/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}
export function normalizePhone(s: string | null | undefined): string {
  const digits = (s ?? '').replace(/[^0-9+]/g, '');
  if (!digits) return '';
  return digits.replace(/^\+49/, '0').replace(/^0049/, '0');
}
export function normalizeEmail(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

export interface DuplicateInput {
  id: number;
  display_name: string;
  email?: string | null;
  mobile?: string | null;
}
/** R12: Kandidat, wenn normalisierter Name gleich oder E-Mail gleich oder Mobilnummer gleich. */
export function duplicateReason(a: DuplicateInput, b: DuplicateInput): 'name' | 'email' | 'mobile' | null {
  if (a.id === b.id) return null;
  const na = normalizeName(a.display_name);
  if (na && na === normalizeName(b.display_name)) return 'name';
  const ea = normalizeEmail(a.email);
  if (ea && ea === normalizeEmail(b.email)) return 'email';
  const ma = normalizePhone(a.mobile);
  if (ma && ma === normalizePhone(b.mobile)) return 'mobile';
  return null;
}
export function findDuplicates<T extends DuplicateInput>(list: T[]): { a: T; b: T; reason: 'name' | 'email' | 'mobile' }[] {
  const out: { a: T; b: T; reason: 'name' | 'email' | 'mobile' }[] = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const r = duplicateReason(list[i]!, list[j]!);
      if (r) out.push({ a: list[i]!, b: list[j]!, reason: r });
    }
  }
  return out;
}

/** R11: Ziel behält eigene Werte, leere Felder werden aus der Quelle gefüllt. */
export function mergeValues<T extends Record<string, unknown>>(target: T, source: T, keys: (keyof T)[]): Partial<T> {
  const patch: Partial<T> = {};
  for (const k of keys) {
    const tv = target[k];
    const sv = source[k];
    const empty = tv === null || tv === undefined || tv === '' || tv === false;
    const hasSource = !(sv === null || sv === undefined || sv === '');
    if (empty && hasSource) patch[k] = sv;
  }
  return patch;
}

/** R4: Vertrag liegt vor: jüngstes Jahr aus Dokumenten der Art Vertrag oder erledigten Vereinbarungspunkten */
export function contractYear(years: (number | null | undefined)[]): number | null {
  const ys = years.filter((y): y is number => typeof y === 'number' && !Number.isNaN(y));
  return ys.length ? Math.max(...ys) : null;
}

/**
 * Vertragsart je Forstkontakt oder Waldbesitzer (Review 26.10.2026): Standard ist der Waldbesitzervertrag; große
 * Betriebe haben eine Kooperationsvereinbarung, bei manchen wird bewusst ohne Vertrag gearbeitet (z. B. 2018 bis 2022).
 */
export const CONTRACT_MODES = ['Waldbesitzervertrag', 'Kooperationsvereinbarung', 'ohne Vertrag'] as const;
export type ContractMode = (typeof CONTRACT_MODES)[number];
export function contractModeOf(attributes: Record<string, Record<string, unknown>> | null | undefined): ContractMode {
  const v = attributes?.forester?.contract_mode ?? attributes?.owner?.contract_mode;
  return (CONTRACT_MODES as readonly string[]).includes(String(v)) ? (v as ContractMode) : 'Waldbesitzervertrag';
}
/** Anzeige der Vertragsspalte: „ohne Vertrag“ fordert nichts nach, nur ein fehlender Waldbesitzervertrag ist rot */
export function contractStatus(mode: ContractMode, year: number | null): { text: string; missing: boolean } {
  if (mode === 'ohne Vertrag') return { text: 'ohne Vertrag', missing: false };
  if (mode === 'Kooperationsvereinbarung') return { text: year ? `Kooperationsvereinbarung (${year})` : 'Kooperationsvereinbarung', missing: false };
  return year ? { text: `liegt vor (${year})`, missing: false } : { text: 'fehlt', missing: true };
}

/** R5: Pflanzbestätigungen über abgeschlossene Aktionen (Aktionstag < heute) */
export function confirmationStats<T extends { id: number; label: string; date: string | null; confirmed: boolean }>(
  actions: T[],
  today: string,
): { done: number; total: number; missing: { id: number; label: string }[] } {
  const past = actions.filter((a) => a.date && a.date < today);
  const missing = past.filter((a) => !a.confirmed).map((a) => ({ id: a.id, label: a.label }));
  return { done: past.length - missing.length, total: past.length, missing };
}

/** R6: Warnhinweis bei Bewertung C */
export function ratingWarning(grade: string | null | undefined, reason?: string | null): string | null {
  if (grade !== 'C') return null;
  return reason?.trim() ? `Bewertung C: ${reason.trim()}` : 'Bewertung C: nicht ohne Rücksprache erneut beauftragen';
}
